import { useSyncExternalStore, useMemo } from 'react';
import * as Y from 'yjs';
import { Awareness } from 'y-protocols/awareness';
import { yDocToJson } from '@glueapp/graph-collab';
import { DataGlueGraph } from '@glueapp/graph-types';

/**
 * Derive a `DataGlueGraph` snapshot from a Y.Doc.
 *
 * Re-derives on any deep change to the doc roots (`nodes`, `connections`, etc.).
 * `selected` is reconstructed from the local user's awareness state since
 * selection isn't part of the doc.
 *
 * Phase 2 returns the legacy JSON shape so existing reducer-driven UI keeps
 * rendering unchanged. Phases 3+ will move callers off the JSON projection.
 */
export function useYDocGraph(yDoc: Y.Doc | null, awareness: Awareness | null): DataGlueGraph | undefined {
    const { subscribe, getSnapshot } = useMemo(() => {
        let cached: DataGlueGraph | undefined;
        let dirty = true;

        const getSnapshot = (): DataGlueGraph | undefined => {
            if (!yDoc) return undefined;
            if (!dirty) return cached;
            // Re-derive on store change. yDocToJson is O(nodes + connections);
            // for Phase 2 graph sizes that's fine. Phase 3 can move to incremental.
            const projection = yDocToJson(yDoc);
            const localSelection = awarenessSelection(awareness);
            cached = mergeSelection(projection, localSelection);
            dirty = false;
            return cached;
        };

        if (!yDoc) {
            return { subscribe: () => () => undefined, getSnapshot };
        }

        const observedTargets = [
            yDoc.getMap('nodes'),
            yDoc.getMap('connections'),
            yDoc.getMap('variables'),
            yDoc.getMap('functions'),
            yDoc.getMap('meta'),
        ];
        const subscribe = (notify: () => void) => {
            const listener = () => {
                dirty = true;
                notify();
            };
            for (const target of observedTargets) {
                target.observeDeep(listener);
            }
            // Only invalidate the projection cache when the LOCAL user's
            // `selectedLegacyIds` changes — that's the only awareness field
            // that maps into the derived graph (`node.selected`). Cursor
            // moves and remote peers' awareness fire `change` at ~20Hz; if
            // we re-derived for those, every cursor wiggle would re-run
            // `yDocToJson` over the entire graph.
            const selectionKey = (): string => {
                const local = awareness?.getLocalState();
                const ids = Array.isArray(local?.selectedLegacyIds) ? (local.selectedLegacyIds as number[]) : [];
                return ids.length === 0
                    ? ''
                    : ids
                          .slice()
                          .sort((a, b) => a - b)
                          .join(',');
            };
            let previousSelectionKey = selectionKey();
            const awarenessListener = () => {
                const next = selectionKey();
                if (next === previousSelectionKey) return;
                previousSelectionKey = next;
                dirty = true;
                notify();
            };
            awareness?.on('change', awarenessListener);
            return () => {
                for (const target of observedTargets) {
                    target.unobserveDeep(listener);
                }
                awareness?.off('change', awarenessListener);
            };
        };

        return { subscribe, getSnapshot };
    }, [yDoc, awareness]);

    return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

function awarenessSelection(awareness: Awareness | null): Set<number> | null {
    if (!awareness) return null;
    const localState = awareness.getLocalState();
    if (!localState || !Array.isArray(localState.selectedLegacyIds)) return null;
    return new Set<number>(localState.selectedLegacyIds as number[]);
}

function mergeSelection(graph: DataGlueGraph, selection: Set<number> | null): DataGlueGraph {
    if (!selection || selection.size === 0) return graph;
    return {
        ...graph,
        nodeInstances: graph.nodeInstances.map(node => (selection.has(node.id) ? { ...node, selected: true } : node)),
        functions: graph.functions.map(fn => ({
            ...fn,
            nodeInstances: fn.nodeInstances.map(node => (selection.has(node.id) ? { ...node, selected: true } : node)),
        })),
    };
}
